<script setup lang="ts">
/**
 * Кандидаты в план — макет 9-1 (301:13907): поиск, три пресета и карточки,
 * которые перетаскивают в стек слева.
 *
 * Пресеты и поиск — не одно и то же поле: пресет отвечает на «покажи, чем я
 * вообще занят», поиск — на «где та задача, про которую я помню только слово».
 * Пока в поиске что-то набрано, пресеты не участвуют: иначе непонятно, чей
 * список на экране.
 *
 * Карточки клонируются, а не переезжают: список кандидатов — витрина, и
 * задача, утащенная в план, из него не пропадает.
 */
import { Search } from 'lucide-vue-next'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import draggable from 'vuedraggable'

import IssueTypeIcon from '@/features/issues/components/IssueTypeIcon.vue'
import { formatDueDate } from '@/features/issues/format'

import { listCandidates, searchCandidates, type Candidate, type CandidatePreset } from '../api'

const props = defineProps<{
  /** Ключи, уже лежащие в плане: их карточки притушены */
  planned: Set<string>
}>()

const emit = defineEmits<{ add: [key: string] }>()

const { t, locale } = useI18n()

const PRESETS: CandidatePreset[] = ['mine', 'hot', 'recent']

/** Пауза перед запросом: поиск идёт по всем проектам, и буква — не повод */
const TYPING_MS = 250

const preset = ref<CandidatePreset>('mine')
const query = ref('')
const items = ref<Candidate[]>([])
const isLoading = ref(false)
const field = ref<HTMLInputElement | null>(null)

let timer: ReturnType<typeof setTimeout> | undefined

const load = async () => {
  isLoading.value = true

  try {
    const text = query.value.trim()

    items.value = text ? await searchCandidates(text) : await listCandidates(preset.value)
  } catch {
    // Панель — подсказка, а не работа: пустой список на отказе честнее, чем
    // ошибка во весь экран поверх плана, который отлично живёт и без неё.
    items.value = []
  } finally {
    isLoading.value = false
  }
}

watch([query, preset], () => {
  clearTimeout(timer)
  timer = setTimeout(() => void load(), TYPING_MS)
})

onBeforeUnmount(() => clearTimeout(timer))

void load()

const dueLabel = (candidate: Candidate) =>
  candidate.dueDate ? formatDueDate(candidate.dueDate, locale.value) : '—'

const isPlanned = (candidate: Candidate) => props.planned.has(candidate.key)

/** Кандидаты для переноса: то же, что на экране — список отдаёт vuedraggable */
const list = computed({
  get: () => items.value,
  // Перенос из витрины ничего в ней не меняет: клонированную карточку Sortable
  // возвращает списку как есть, и вернуть его на место — вся нужная реакция.
  set: (next: Candidate[]) => {
    items.value = next
  },
})

defineExpose({
  focus: () => {
    field.value?.focus()
  },
})
</script>

<template>
  <section class="cand">
    <label class="cand__search">
      <Search class="cand__search-icon" :size="14" aria-hidden="true" />
      <input
        ref="field"
        v-model="query"
        class="cand__input"
        type="search"
        :placeholder="t('plan.candidates.search')"
        :aria-label="t('plan.candidates.search')"
      />
    </label>

    <div class="cand__presets">
      <button
        v-for="value in PRESETS"
        :key="value"
        type="button"
        class="cand__preset"
        :class="{ 'cand__preset--active': !query.trim() && preset === value }"
        :aria-pressed="!query.trim() && preset === value"
        @click="query = ''; preset = value"
      >
        {{ t(`plan.candidates.presets.${value}`) }}
      </button>
    </div>

    <draggable
      v-model="list"
      class="cand__list"
      item-key="id"
      :group="{ name: 'plan', pull: 'clone', put: false }"
      :sort="false"
      :animation="180"
      :force-fallback="true"
      :fallback-on-body="true"
      :fallback-tolerance="4"
      ghost-class="ccard--ghost"
      drag-class="ccard--drag"
    >
      <template #item="{ element }">
        <button
          type="button"
          class="ccard"
          :class="{ 'ccard--planned': isPlanned(element) }"
          :title="element.title"
          @click="emit('add', element.key)"
        >
          <IssueTypeIcon class="ccard__type" :type="element.type" />
          <span class="ccard__key">{{ element.key }}</span>
          <span class="ccard__title">{{ element.title }}</span>
          <span class="ccard__due">{{ dueLabel(element) }}</span>
        </button>
      </template>
    </draggable>

    <p v-if="!items.length && !isLoading" class="cand__hint">{{ t('plan.candidates.empty') }}</p>
    <p v-else class="cand__hint">{{ t('plan.candidates.hint') }}</p>
  </section>
</template>

<style scoped>
.cand {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border-radius: var(--radius-md);
  background: var(--color-bg-muted);
}

.cand__search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
}

.cand__search-icon {
  flex-shrink: 0;
  color: var(--color-text-muted);
}

.cand__input {
  flex: 1;
  min-width: 0;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-primary);
  font-family: inherit;
  font-size: var(--font-size-body-sm);
  line-height: 18px;
  outline: none;
}

.cand__input::placeholder {
  color: var(--color-text-muted);
}

.cand__presets {
  display: flex;
  gap: 6px;
}

.cand__preset {
  padding: 4px 8px;
  /* Токена на 6 в шкале радиусов нет: ближайший --radius-sm (8) на пилюле
     высотой 24 заметно круглее макета. */
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-family: inherit;
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
  cursor: pointer;
}

.cand__preset--active {
  border-color: transparent;
  background: var(--color-primary-tint);
  color: var(--color-primary);
}

.cand__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ccard {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  font-family: inherit;
  text-align: left;
  cursor: grab;
}

/* Уже в плане — карточка притушена: второй раз её тащить незачем, но и
   прятать нельзя, иначе список прыгает под рукой. */
.ccard--planned {
  opacity: 0.45;
}

.ccard--ghost {
  opacity: 0.4;
}

.ccard--drag {
  box-shadow: var(--shadow-md);
}

.ccard__type {
  flex-shrink: 0;
}

.ccard__key {
  flex-shrink: 0;
  color: var(--color-text-secondary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-mono);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
}

.ccard__title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  line-height: 18px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.ccard__due {
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  line-height: 16px;
}

.cand__hint {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  line-height: 16px;
}
</style>
